import { Box, Stack, Typography } from "@mui/material";
import { useAddHunterRoleMutation } from "Api/Mutations/Roles/useAddHunterRoleMutation";
import { HunterEULACheckboxLabel } from "Components/Auth/SignUp/Hunter/HunterEULACheckboxLabel";
import { StatusIndicator } from "Components/Hunter/Merchants/NewRegistration/StatusIndicator";
import { getAddRoleErrorResource } from "Components/Roles/getAddRoleErrorResource";
import { useSwitchToNewRole } from "Components/Roles/useSwitchToNewRole";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { FormSection } from "Components/Shared/FormSection";
import { CheckboxInput } from "Components/Shared/Inputs/CheckboxInput";
import { Dropdown } from "Components/Shared/Inputs/Form/Dropdown";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useRegionList } from "Hooks/Options/useRegionList";
import { useAres } from "Hooks/useAres";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import type { PartyDto, Region } from "Infrastructure/Api/Api";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { LocalStorageKeys } from "Utils/Storage/LocalStorage/LocalStorageKeys";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
} from "Utils/Storage/LocalStorage/SecureLocalStorage";

const COMPONENT_RESOURCES = Resources.Roles.AddRole;

type FormModel = {
  companyNumber: string;
  region?: Region;
};

export const AddHunterRoleForm = () => {
  const { t } = useResource();
  const user = useLoggedInUser();
  const { codeListRegions } = useRegionList();
  const [aresData, setAresData] = useState<PartyDto | null>(null);
  // Registration is Customer-first, so the hunter franchise contract (formerly agreed on the
  // hunter sign-up) is agreed here — at the moment the role is actually requested.
  const [agreesWithHunterTerms, setAgreesWithHunterTerms] = useState(false);

  const { handleSubmit, control, watch, setValue } = useForm<FormModel>({
    // The hunter market is an explicit choice, defaulting to the person's market.
    defaultValues: { companyNumber: "", region: user?.region ?? undefined },
  });
  const companyNumber = watch("companyNumber");
  const region = watch("region");

  const { isFetchingAres, isError: isAresError } = useAres({
    identifierNumber: companyNumber,
    region,
    onAresData: setAresData,
  });

  const handleRegionChange = () => {
    // The register result belongs to the other country's register — start over.
    setAresData(null);
    setValue("companyNumber", "");
  };

  const { switchToRole, isSwitching } = useSwitchToNewRole();

  const { mutate: addHunterRole, isPending: isAdding } =
    useAddHunterRoleMutation({
      onSuccess: (result) => {
        toast.success(t(COMPONENT_RESOURCES.Form.Success));
        removeLocalStorageItem(LocalStorageKeys.AffiliateCode);
        switchToRole(result.identityUserPublicID);
      },
      onError: (error) => {
        toast.error(t(getAddRoleErrorResource(error)));
      },
    });

  const onSubmit = () => {
    if (!aresData || !region) {
      return;
    }

    addHunterRole({
      // The register payload carries no region — stamp the selected market onto the company.
      company: { ...aresData, region },
      affiliateCode: getLocalStorageItem(LocalStorageKeys.AffiliateCode),
    });
  };

  const formattedCompanyName =
    (companyNumber.length === 8 && aresData?.companyName) || null;
  const isLoading = isFetchingAres || isAdding || isSwitching;
  const isSubmitDisabled =
    isLoading || !region || !aresData || isAresError || !agreesWithHunterTerms;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <PrimaryPaper>
        <Stack gap={2}>
          <Typography variant="body2" color="text.secondary">
            {t(COMPONENT_RESOURCES.HunterForm.Description)}
          </Typography>
          <FormSection>
            <Dropdown
              codeList={codeListRegions}
              control={control}
              name="region"
              label={t(COMPONENT_RESOURCES.Form.Region)}
              onChange={handleRegionChange}
            />
            <FormInput
              name="companyNumber"
              control={control}
              label={t(COMPONENT_RESOURCES.Form.CompanyNumber)}
              disabled={isLoading || !region}
              error={isAresError}
            />
            <Box display="flex" alignItems="center">
              <StatusIndicator
                isInvalid={isAresError}
                company={formattedCompanyName}
                isLoadingCompany={isFetchingAres}
                isCompanyAvailable={!!aresData}
              />
            </Box>
          </FormSection>
          <CheckboxInput
            name="agreesWithHunterTerms"
            label={<HunterEULACheckboxLabel />}
            checked={agreesWithHunterTerms}
            onChange={() => setAgreesWithHunterTerms((prev) => !prev)}
          />
          <PrimaryButton
            fullWidth
            type="submit"
            disabled={isSubmitDisabled}
            isLoading={isAdding || isSwitching}
            variant="contained"
            color="primary"
          >
            {t(COMPONENT_RESOURCES.Form.Submit)}
          </PrimaryButton>
        </Stack>
      </PrimaryPaper>
    </form>
  );
};
